
import { Component, Input, forwardRef, OnInit, AfterViewInit, OnChanges, ViewChild, ElementRef, Injector, Output, EventEmitter, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NG_VALIDATORS, AbstractControl, ValidationErrors, Validator, NgControl, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon'
import { DecimalPipe, CurrencyPipe } from '@angular/common';

@Component({
  selector: 'rms-input',
  templateUrl: './input-field.component.html',
  styleUrls: ['../rms-styles.scss', './input-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputFieldComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => InputFieldComponent),
      multi: true
    },
    DecimalPipe,
    CurrencyPipe
  ],
  imports: [CommonModule, FormsModule, MatIconModule]
})
export class InputFieldComponent implements ControlValueAccessor, OnInit, AfterViewInit, OnChanges, Validator, OnDestroy {
  @Input() search: boolean = false;
  @Input() label?: string = undefined;
  @Input() type: 'text' | 'search' | 'currency'  | 'password' | 'decimal' | 'percent' | 'email' = 'text';
  @Input() placeholder?: string = this.label ?? '';
  @Input() disabled: boolean = false;
  @Input() value: any;
  @Input() required: boolean = false;
  @Input() decimalFormat: string = '1.2-4';
  @Input() min?: any;
  @Input() max?: any;
  @Input() step?: any = 1;
  @Input() currencyCode: string = 'USD';
  @Input() locale: string = 'en-US';
  @Input() suffix: string = '';
  @Input() noMargin: boolean = false;
  @Input() clearZeroOnFocus: boolean = false;
  @Input() roundOnBlur: boolean = false;
  @Input() rounding: 'round' | 'truncate' | 'floor' | 'ceil' = 'round';
  @Input() backgroundColor: string = 'white';
  @Input() focusOnLoad: boolean = false;
  @Input() LargeInput: boolean = false;
  @Input() debounce: number = 0;
  @ViewChild('nativeInput', { read: ElementRef }) nativeInput?: ElementRef<HTMLInputElement>

  @Output() valueChange = new EventEmitter<any>();
  showPassword: boolean = false;
  formattedValue: string = '';
  rawInput: string = '';
  focused: boolean = false;
  public ngControl: NgControl | null = null;
  onChange = (_: any) => { };
  onTouched = () => { };
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  constructor(private decimalPipe: DecimalPipe, private currencyPipe: CurrencyPipe, private injector: Injector) {
  }

  ngOnDestroy(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }
  validate(control: AbstractControl): ValidationErrors | null {
    // Only validate when type is email
    if (this.type !== 'email') return null;

    const v = (control.value ?? '').toString().trim();

    // If not required and empty: valid
    if (!v) return null;

    // WHATWG-ish email (similar spirit to Angular's built-in email validator)
    const emailRegex =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;

    return emailRegex.test(v) ? null : { email: true };
  }

  get invalid(): boolean {
    return !!(
      this.ngControl &&
      this.ngControl.invalid &&
      (this.ngControl.touched || this.ngControl.dirty)
    );
  }
  get errorMessage(): string | null {
    if (!this.errors) return null;

    if (this.errors['required']) {
      return 'This field is required.';
    }

    if (this.errors['email']) {
      return 'Please enter a valid email address.';
    }

    // fallback for future validators
    return 'Invalid value.';
  }
  get errors(): ValidationErrors | null {
    return this.ngControl?.errors ?? null;
  }

  getNativeType(): string {
    if (this.search) return 'search';
    if (this.type === 'password') return this.showPassword ? 'text' : 'password';
    if (this.type === 'email') return 'email';
    // your numeric “types” should still be text so you can format
    if (this.isNumericType) return 'text';
    return 'text';
  }

  get hasLabel(): boolean {
    return this.label === undefined ? false : true
  }

  private inputsReady = false;

  ngOnInit() {
    // Mark inputs as ready on first init
    this.inputsReady = true;
    this.reformatIfReady();
  }

  ngAfterViewInit(): void {
    this.tryFocus();
    this.ngControl = this.injector.get(NgControl as any, null as any, { self: true, optional: true } as any);
  }

  public focus(): void {
    this.tryFocus(true);
  }

  private tryFocus(force = false): void {
    if ((!this.focusOnLoad && !force) || this.disabled) return;
    // Next tick ensures DOM is ready
    setTimeout(() => {
      const el = this.nativeInput?.nativeElement;
      if (!el) return;
      el.focus({ preventScroll: true });
      // Optional: place caret at end
      try {
        const len = el.value?.length ?? 0;
        el.setSelectionRange(len, len);
      } catch { }
    }, 0);
  }

  ngOnChanges(changes: any): void {
    // If focusOnLoad toggles on, schedule focus on next tick
    if (changes['focusOnLoad']) {
      queueMicrotask(() => this.tryFocus());
    }
  }

  private reformatIfReady() {
    if (!this.inputsReady) return;
    if (!this.focused) this.rawInput = this.formatValue(this.value);
  }

  writeValue(value: any): void {
    // normalize empty string from parent forms into null
    this.value = (value === '' ? null : value);

    this.rawInput = this.inputsReady && !this.focused
      ? this.formatValue(this.value)          // -> '' when null
      : ((this.value ?? '') as any).toString();
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  handleInputChange(event: any) {
    const input = (event.target?.value ?? '').toString();

    // Always update what the user visually sees immediately
    this.rawInput = input;

    let parsedValue: any;

    if (this.isNumericType) {
      parsedValue = this.parseValue(input);
    } else {
      parsedValue = input;
    }

    // Keep the component's internal value current
    this.value = parsedValue;

    // No debounce configured -> existing behavior
    if (!this.debounce || this.debounce <= 0) {
      this.commitValue(parsedValue);
      return;
    }

    // Cancel the previous pending update
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Wait until the user stops typing
    this.debounceTimer = setTimeout(() => {
      this.commitValue(parsedValue);
    }, this.debounce);
  }
  private commitValue(value: any): void {
    this.onChange(value);
    this.valueChange.emit(value);
  }
  formatValue(value: any): string {
    if (value == null || value === '') return '';

    if (
      this.type === 'text' ||
      this.type === 'search' ||
      this.type === 'password' ||
      this.type === 'email'
    ) {
      return value.toString();
    }

    const num = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(num)) return '';

    const { minFrac, maxFrac } = this.parseDigitsInfo(this.decimalFormat);
    const rounded = this.applyRounding(num, maxFrac);

    switch (this.type) {
      case 'currency':
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currencyCode,
          minimumFractionDigits: minFrac,
          maximumFractionDigits: maxFrac
        }).format(rounded);

      case 'decimal':
        return new Intl.NumberFormat(this.locale, {
          minimumFractionDigits: minFrac,
          maximumFractionDigits: maxFrac
        }).format(rounded);

      case 'percent':
        return (
          new Intl.NumberFormat(this.locale, {
            minimumFractionDigits: minFrac,
            maximumFractionDigits: maxFrac
          }).format(rounded) + '%'
        );

      default:
        return value.toString();
    }
  }
  parseValue(value: string): number | null {
    const cleaned = (value ?? '').replace(/[^0-9.-]+/g, '').trim();
    // if the user deleted or left an incomplete numeric like '-' or '.'
    if (cleaned === '' || cleaned === '-' || cleaned === '.' || cleaned === '-.') {
      return null;
    }
    const num = Number(cleaned);
    return Number.isFinite(num) ? num : null;
  }
  private pointerFocus = false;
  onPointerDown() { this.pointerFocus = true; }

  onFocus(event?: FocusEvent) {
    this.focused = true;
    this.rawInput = this.clearZeroOnFocus && this.value != null && this.value !== '' && Number(this.value) === 0
      ? '' : (this.value ?? '').toString();
    const input = event?.target as HTMLInputElement | null;
    if (input && !this.pointerFocus) {
      input.value = this.rawInput;
      input.select();
    }
    this.pointerFocus = false;
  }

  onBlur() {
    this.pointerFocus = false;
    this.focused = false;
    if (this.roundOnBlur && this.isNumericType && this.value != null && this.value !== '') {
      const num = Number(this.value);
      if (Number.isFinite(num)) {
        const rounded = this.applyRounding(num, this.parseDigitsInfo(this.decimalFormat).maxFrac);
        if (rounded !== num) { this.value = rounded; this.commitValue(rounded); }
      }
    }
    this.rawInput = this.formatValue(this.value); // '' when null
    this.onTouched();
  }
  formatOnBlur() {
    this.rawInput = this.formatValue(this.value);
  }
  increment() {
    const current = this.getNumericValue();
    if (current === null) return;

    let newValue = current + Number(this.step);
    if (this.max != null) newValue = Math.min(newValue, this.max);
    this.setValue(newValue);
  }

  decrement() {
    const current = this.getNumericValue();
    if (current === null) return;

    let newValue = current - Number(this.step);
    if (this.min != null) newValue = Math.max(newValue, this.min);
    this.setValue(newValue);
  }
  getNumericValue(): number | null {
    if (this.value === null || this.value === '') return null;
    return typeof this.value === 'number' ? this.value : (Number(this.value) || null);
  }

  setValue(val: number | null) {
    this.value = val;
    this.rawInput = this.focused ? (val ?? '').toString() : this.formatValue(val);
    this.onChange(val);
  }
  handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.increment();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.decrement();
    }
  }
  private applyRounding(val: number, maxFrac: number): number {
    const f = Math.pow(10, maxFrac);
    switch (this.rounding) {
      case 'truncate': return Math.trunc(val * f) / f;
      case 'floor': return Math.floor(val * f) / f;
      case 'ceil': {
        const scaled = val * f;
        const nearest = Math.round(scaled);
        // Avoid an extra cent caused solely by floating point representation.
        const stable = Math.abs(scaled - nearest) <= Number.EPSILON * Math.max(1, Math.abs(scaled)) * 4 ? nearest : scaled;
        return Math.ceil(stable) / f;
      }
      default: return Math.round(val * f) / f;
    }
  }

  private parseDigitsInfo(digits: string): { minInt: number; minFrac: number; maxFrac: number } {
    // format: "minInt.minFrac-maxFrac"
    const [i, frac] = digits.split('.');
    const [minF, maxF] = (frac ?? '0-0').split('-');
    return { minInt: +(i ?? 1), minFrac: +(minF ?? 0), maxFrac: +(maxF ?? +(minF ?? 0)) };
  }
  private get isNumericType() {
    return this.type === 'currency' || this.type === 'decimal' || this.type === 'percent';
  }

  clearSearch() {
    this.rawInput = '';
    this.value = null;

    this.valueChange.emit(null);
    // notify form control
    this.onChange(null);
    this.onTouched();
  }

}
